import React, {Fragment} from 'react';
import {Collaboration, Transition, TransitionGroup, Popup, Form, Submit, Edit, Button, Icon, Flex, Card, Layout, Separator} from 'components';
import {noop, flow, api, map, modal, openPopup, useNamedSelector, useToggle, moment} from 'helpers';
import {store} from 'store';
import {forbid} from 'store/session';

export function DashboardSidebar() {
  const time = useNamedSelector('session.time');
  const {premium} = useNamedSelector('session.subscription');
  const collaborators = useNamedSelector('collaborators.list');
  return (
    <Layout.Sidebar>
      <Flex className={['justified', 'middle']}>
        <h4>Collaborators</h4>
        <Icon
          name="plus-large"
          className="secondary"
          onClick={premium ? openPopup(Invite) : forbid}
        />
      </Flex>
      <Layout.Sidebar.Scrollable>
        { map(collaborators.confirmed, user =>
          <Card key={user.id} navLink={`/settings/sharing/${user.id}/`} id={user.id}>
            <Card.Body>
              <Card.Text>
                { user.name
                  ? <b>{user.name}</b>
                  : <u className="link" onClick={openPopup(AddName, {user})}>Add name</u>
                }
              </Card.Text>
              <Card.Note>{ user.email }</Card.Note>
            </Card.Body>
            <Card.Toolbar aria-haspopup="false">
              <Icon name="pencil" onClick={openPopup(AddName, {user})} />
              <Icon name="share" onClick={modal(Collaboration.SharePanelModal, {user})} />
            </Card.Toolbar>
          </Card>
        ) }
        { collaborators.pending?.length > 0 &&
          <Fragment>
            <Separator heading="Pending Collaborators" />
            <TransitionGroup component={null}>
              { collaborators.pending.map(user =>
                <Transition key={user.id} timeout={300}>
                  { state =>
                    <Card className={state}>
                      <Card.Body>
                        <Card.Note>{ user.email }</Card.Note>
                        {user.lastInvited &&
                          <Card.Note className="secondary">invited { moment.unix(user.lastInvited).fromNow() }</Card.Note>
                        }
                      </Card.Body>
                      <Card.Toolbar aria-haspopup="false">
                        {(!user.lastInvited || time.unix() - user.lastInvited > 60 * 60 * 2) &&
                          <Icon name="mail" Sub="return" onClick={openPopup(Resend, {user})} />
                        }
                        <Icon name="trash" onClick={openPopup(RemoveUser, {user})} />
                      </Card.Toolbar>
                    </Card>
                  }
                </Transition>
              ) }
            </TransitionGroup>
          </Fragment>
        }
        {!collaborators.confirmed && !collaborators.pending &&
          <p>You have to collaborators.</p>
        }
      </Layout.Sidebar.Scrollable>
    </Layout.Sidebar>
  );
}

function Resend({user, popup}) {
  const [sending, toggle] = useToggle();
  const send = () => {
    toggle();
    invite(user).then(popup.close);
  };
  return (
    <Popup.Menu onClick={noop}>
      <b>Resend invitation?</b>
      <Popup.Menu.Item icon={sending ? 'spinner' : 'check'} className="primary" onClick={sending ? noop : send}>Confirm</Popup.Menu.Item>
      <Popup.Menu.Item icon="close" onClick={popup.close}>Cancel</Popup.Menu.Item>
    </Popup.Menu>
  );
}

function RemoveUser({ user, popup }) {
  return (
    <Popup.Menu>
      <b>Remove?</b>
      <Popup.Menu.Item icon="check" className="danger" onClick={() => store.dispatch({ type: 'COLLABORATORS_REMOVE', id: user.id })}>Confirm</Popup.Menu.Item>
      <Popup.Menu.Item icon="close" onClick={popup.close}>Cancel</Popup.Menu.Item>
    </Popup.Menu>
  );
}

function AddName({ user, popup }) {
  const onSubmit = ({ name }) => {
    store.dispatch({ type: 'COLLABORATORS_SET_NAME', user, name });
    popup.close();
  }
  return (
    <Form className="compact-popup" delay onSubmit={onSubmit} initialValues={{ name: user.name }}>
      <Edit required autoFocus name="name" placeholder="Enter name.." />
      <Submit icon="check" />
    </Form>
  );
}

function Invite({ popup }) {
  popup.setClassName('compact');
  return (
    <Form className="compact-popup" onSubmit={flow(invite, promise => promise.then(popup.redirect(Invited)))} delay>
      <Edit required autoFocus name="email" type="email" placeholder="Collaborator's email.." />
      <Submit icon="check" />
    </Form>
  );
}

function Invited({ popup }) {
  return (
    <div style={{ maxWidth: '18em' }}>
      <p>An invitation has been sent. Once the recipient accepts it, you will be able to collaborate with him/her.</p>
      <Flex className={['right', 'small-spacing', 'small-gutter', 'small']}>
        <Button icon="plus" className={['borderless', 'small']} onClick={popup.redirect(Invite)}>Invite Another</Button>
        <Button icon="check" className={['small']} onClick={popup.close}>Ok</Button>
      </Flex>
    </div>
  );
}

async function invite({ email }) {
  try {
    await api('/invite-to-collaborate', {
      emails: [ email ]
    });
    store.dispatch({
      type: 'COLLABORATORS_INVITE',
      email,
    });
  } catch (error) {
    store.dispatch({
      type: 'UI_ERROR',
      errorMessage: 'Failed to add a collaborator. Please try again later.',
      error,
    });
  }
}
